import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, IconButton, Select, MenuItem, Checkbox, ListItemText,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddIcon from '@mui/icons-material/Add';
import CustomDatePicker from '../form-components/CustomDatePicker';
import { getAdminEntities } from '../../api/paymentApi';
import { dropdownMenuProps, selectFieldSx } from '../../utils/dropdownStyles';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { useSpaceId } from '../../hooks/useSpaceId';
import { formatAmount } from '../../utils/currencyFormat';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
// Same shape as TransactionTable.jsx's own multi-select — kept as its own copy here
// rather than a shared import, matching this codebase's existing convention.
const multiSelectSx = {
  ...selectFieldSx,
  fontSize: 13,
  minWidth: 180,
  maxWidth: 260,
  '& .MuiSelect-select': { py: '6.5px' },
};


const ENTITY_SPACE_SELECT_W = 260;
const entitySpaceSelectSx = {
  ...multiSelectSx,
  minWidth: 0,
  maxWidth: 'none',
  width: { xs: '100%', sm: ENTITY_SPACE_SELECT_W },
  '& .MuiSelect-select': {
    py: '6.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
const entitySpaceMenuProps = {
  ...dropdownMenuProps,
  PaperProps: {
    ...dropdownMenuProps.PaperProps,
    sx: { ...dropdownMenuProps.PaperProps?.sx, width: { sm: ENTITY_SPACE_SELECT_W } },
  },
};

// List view of the Deposits tab — one row per Deposit ID. Mirrors TransactionTable.jsx's
// toolbar/filter/pagination conventions so the two tabs feel like one product.
const DepositsTable = ({
  fetchFn, showGlobalFilters = false, canAddDeposit = false, onAddDeposit, onSelectDeposit,
}) => {
  const spaceId = useSpaceId();
  const [deposits, setDeposits] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [committedDateRange, setCommittedDateRange] = useState({ from: '', to: '' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  // Entity/Space filters — gms_accountant's global view only, same pattern as
  // TransactionTable.jsx's own entity/space multi-select.
  const [entities,          setEntities]          = useState([]);
  const [loadingEntities,   setLoadingEntities]   = useState(false);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [selectedSpaceIds,  setSelectedSpaceIds]  = useState([]);

  const searchPlaceholder = useLocalText('Search Deposit ID...');
  const isDateRangeError = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Load entities once when the global filter panel is shown
  useEffect(() => {
    if (!showGlobalFilters) return;
    setLoadingEntities(true);
    getAdminEntities()
      .then((data) => setEntities(Array.isArray(data) ? data : []))
      .catch(() => setEntities([]))
      .finally(() => setLoadingEntities(false));
  }, [showGlobalFilters]);

  // Spaces available for the space multi-select (filtered by selected entities)
  const availableSpaces = useMemo(() => {
    if (!entities.length) return [];
    const pool = selectedEntityIds.length
      ? entities.filter((e) => selectedEntityIds.includes(e.id))
      : entities;
    return pool.flatMap((e) => e.spaces ?? []);
  }, [entities, selectedEntityIds]);

  // When the available spaces change, drop any selected spaces that no longer exist
  useEffect(() => {
    if (!selectedSpaceIds.length) return;
    const availableIds = availableSpaces.map((s) => s.id);
    setSelectedSpaceIds((prev) => prev.filter((id) => availableIds.includes(id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSpaces]);

  // spaces selected → pass spaceIds (most specific); entities only → pass entityIds
  // (backend resolves to their spaces); nothing selected → pass neither (all data).
  const apiFilterParams = useMemo(() => {
    if (selectedSpaceIds.length > 0)  return { spaceIds: selectedSpaceIds };
    if (selectedEntityIds.length > 0) return { entityIds: selectedEntityIds };
    return {};
  }, [selectedSpaceIds, selectedEntityIds]);

  useEffect(() => { setPage(0); }, [search, committedDateRange, apiFilterParams, pageSize]);

  const buildFilterParams = useCallback(() => ({
    search: search || undefined,
    dateFrom: committedDateRange.from || undefined,
    dateTo: committedDateRange.to || undefined,
    ...(showGlobalFilters ? apiFilterParams : {}),
  }), [search, committedDateRange, showGlobalFilters, apiFilterParams]);

  const fetchDeposits = useCallback(() => {
    if (!spaceId && !showGlobalFilters) return;
    setLoading(true);
    fetchFn({ page, pageSize, ...buildFilterParams() })
      .then((data) => {
        setDeposits(Array.isArray(data) ? data : data?.items ?? []);
        setTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => {
        setDeposits([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [spaceId, showGlobalFilters, fetchFn, page, pageSize, buildFilterParams]);

  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleDateFromChange = (val) => {
    setDateFrom(val);
    if (!val && !dateTo) setCommittedDateRange({ from: '', to: '' });
  };
  const handleDateToChange = (val) => {
    setDateTo(val);
    if (dateFrom && val && dateFrom <= val) setCommittedDateRange({ from: dateFrom, to: val });
    else if (!val) setCommittedDateRange({ from: '', to: '' });
  };

  const resetFilters = () => {
    setSearchInput(''); setSearch('');
    setDateFrom(''); setDateTo(''); setCommittedDateRange({ from: '', to: '' });
    setSelectedEntityIds([]); setSelectedSpaceIds([]);
    setPage(0);
  };

  const colHead = { color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.75, bgcolor: '#f8fafc' };
  const colCell = { fontSize: 12, color: '#1e293b', py: 1.5, borderBottom: '1px solid #f1f5f9' };
  const columnCount = 4 + (showGlobalFilters ? 4 : 1);

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <Box sx={{ borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography fontWeight={700} fontSize={16} sx={{ color: '#1e293b' }}>
              <LocalText>Deposits</LocalText>
            </Typography>
            <Typography fontSize={12} sx={{ color: '#94a3b8', mt: 0.25 }}>
              <LocalText>One row per Deposit ID. Click any row to drill into its transactions and document.</LocalText>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={<LocalText>Reload data</LocalText>}>
              <IconButton size="small" onClick={fetchDeposits} sx={{ border: '1px solid #dde6f0', borderRadius: '10px', width: 36, height: 36, color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                <RefreshIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={<LocalText>Reset all filters</LocalText>}>
              <IconButton size="small" onClick={resetFilters} sx={{ border: '1px solid #dde6f0', borderRadius: '10px', width: 36, height: 36, color: '#64748b', '&:hover': { bgcolor: '#fff1f2', color: '#dc2626', borderColor: '#fecaca' } }}>
                <FilterAltOffIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            {canAddDeposit && (
              <Box
                component="button"
                onClick={onAddDeposit}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, height: 36, border: 'none', borderRadius: '10px', bgcolor: '#0a326d', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'white', '&:hover': { bgcolor: '#0d3d85' } }}
              >
                <AddIcon sx={{ fontSize: 17 }} /><LocalText>Add Deposit</LocalText>
              </Box>
            )}
          </Box>
        </Box>

        {/* Filter rows */}
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: { sm: 'space-between' }, flexWrap: { sm: 'wrap' }, gap: 1.5 }}>
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: '1 1 200px' }, maxWidth: { sm: 320 }, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, '& fieldset': { borderColor: '#dde6f0' }, '&:hover fieldset': { borderColor: '#5383b3' }, '&.Mui-focused fieldset': { borderColor: '#0a326d', borderWidth: 2 } } }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontSize={11} fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' }, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  <LocalText>From</LocalText>
                </Typography>
                <CustomDatePicker value={dateFrom} max={dateTo || undefined} onChange={(e) => handleDateFromChange(e.target.value)} placeholder="Start date" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontSize={11} fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' }, color: isDateRangeError ? '#ef4444' : '#94a3b8', whiteSpace: 'nowrap' }}>
                  <LocalText>To</LocalText>
                </Typography>
                <CustomDatePicker value={dateTo} min={dateFrom || undefined} onChange={(e) => handleDateToChange(e.target.value)} placeholder="End date" isError={isDateRangeError} />
              </Box>
              {isDateRangeError && (
                <Typography fontSize={11} sx={{ color: '#ef4444', whiteSpace: 'nowrap' }}>
                  <LocalText>Start date must be before end date</LocalText>
                </Typography>
              )}

              {showGlobalFilters && (
                <>
                  <Select
                    size="small"
                    multiple
                    displayEmpty
                    value={selectedEntityIds}
                    onChange={(e) => setSelectedEntityIds(e.target.value)}
                    disabled={loadingEntities}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Entities</span>
                        : entities.filter((e) => selected.includes(e.id)).map((e) => e.name).join(', ')
                    }
                    sx={entitySpaceSelectSx}
                    MenuProps={entitySpaceMenuProps}
                  >
                    {entities.map((entity) => (
                      <MenuItem key={entity.id} value={entity.id} dense>
                        <Checkbox checked={selectedEntityIds.includes(entity.id)} size="small" sx={{ py: 0.25 }} />
                        <ListItemText primary={entity.name} primaryTypographyProps={{ fontSize: 13 }} />
                      </MenuItem>
                    ))}
                    {!loadingEntities && entities.length === 0 && (
                      <MenuItem disabled dense>
                        <ListItemText primary="No entities available" primaryTypographyProps={{ fontSize: 13, color: '#94a3b8' }} />
                      </MenuItem>
                    )}
                  </Select>

                  <Select
                    size="small"
                    multiple
                    displayEmpty
                    value={selectedSpaceIds}
                    onChange={(e) => setSelectedSpaceIds(e.target.value)}
                    disabled={loadingEntities || availableSpaces.length === 0}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? <span style={{ color: '#94a3b8', fontSize: 13 }}>All Spaces</span>
                        : availableSpaces.filter((s) => selected.includes(s.id)).map((s) => s.name).join(', ')
                    }
                    sx={entitySpaceSelectSx}
                    MenuProps={entitySpaceMenuProps}
                  >
                    {availableSpaces.map((space) => (
                      <MenuItem key={space.id} value={space.id} dense>
                        <Checkbox checked={selectedSpaceIds.includes(space.id)} size="small" sx={{ py: 0.25 }} />
                        <ListItemText primary={space.name} primaryTypographyProps={{ fontSize: 13 }} />
                      </MenuItem>
                    ))}
                    {availableSpaces.length === 0 && (
                      <MenuItem disabled dense>
                        <ListItemText
                          primary={selectedEntityIds.length ? 'No spaces in selected entities' : 'Select an entity first'}
                          primaryTypographyProps={{ fontSize: 13, color: '#94a3b8' }}
                        />
                      </MenuItem>
                    )}
                  </Select>

                  {(selectedEntityIds.length > 0 || selectedSpaceIds.length > 0) && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: '#eff6ff', fontSize: 11, fontWeight: 700, color: '#0a326d' }}>
                      {selectedSpaceIds.length > 0
                        ? `${selectedSpaceIds.length} ${selectedSpaceIds.length === 1 ? 'space' : 'spaces'}`
                        : `${selectedEntityIds.length} ${selectedEntityIds.length === 1 ? 'entity' : 'entities'}`
                      }
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Table ───────────────────────────────────────── */}
      <Box sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress sx={{ color: '#0a326d' }} />
          </Box>
        ) : (
          <Table sx={{ minWidth: { xs: 900, md: showGlobalFilters ? 1400 : 900 } }}>
            <TableHead>
              <TableRow>
                {showGlobalFilters && <TableCell sx={colHead}><LocalText>Entity</LocalText></TableCell>}
                {showGlobalFilters && <TableCell sx={colHead}><LocalText>Entity ID</LocalText></TableCell>}
                {showGlobalFilters && <TableCell sx={colHead}><LocalText>Space</LocalText></TableCell>}
                {showGlobalFilters && <TableCell sx={colHead}><LocalText>Space ID</LocalText></TableCell>}
                {!showGlobalFilters && <TableCell sx={colHead}><LocalText>Space</LocalText></TableCell>}
                <TableCell sx={colHead}><LocalText>Deposit ID</LocalText></TableCell>
                <TableCell sx={colHead}><LocalText>Currency</LocalText></TableCell>
                <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Total Amount</LocalText></TableCell>
                <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Total Transactions</LocalText></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {deposits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 6, color: '#94a3b8', fontSize: 13 }}>
                    <LocalText>No deposits found.</LocalText>
                  </TableCell>
                </TableRow>
              ) : (
                deposits.map((dep) => (
                  <TableRow
                    key={`${dep.spaceId}-${dep.depositId}`}
                    onClick={() => onSelectDeposit(dep)}
                    sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#eff6ff' }, '&:last-child td': { border: 0 } }}
                  >
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} fontWeight={600} sx={{ color: '#1e293b' }}>{dep.entityName ?? '—'}</Typography>
                      </TableCell>
                    )}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>{dep.entityId ?? '—'}</Typography>
                      </TableCell>
                    )}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#475569' }}>{dep.spaceName ?? '—'}</Typography>
                      </TableCell>
                    )}
                    {showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>{dep.spaceId ?? '—'}</Typography>
                      </TableCell>
                    )}
                    {!showGlobalFilters && (
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#475569' }}>{dep.spaceName ?? '—'}</Typography>
                      </TableCell>
                    )}

                    <TableCell sx={colCell}>
                      <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: '#f1f5f9', fontSize: 11, fontWeight: 700, color: '#0a326d', fontFamily: 'monospace', textDecoration: 'underline' }}>
                        {dep.depositId}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...colCell, color: '#64748b' }}>{dep.currency ?? '—'}</TableCell>
                    <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Typography fontSize={13} fontWeight={700} sx={{ color: '#1e293b' }}>{formatAmount(dep.totalAmount, dep.currency)}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...colCell, textAlign: 'right' }}>{dep.chargeCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* ── Pagination ──────────────────────────────────── */}
      {!loading && (
        <Box sx={{ p: 3, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize={12} sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                <LocalText>Rows per page</LocalText>
              </Typography>
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                sx={{ ...selectFieldSx, fontSize: 12, minWidth: 68 }}
                MenuProps={dropdownMenuProps}
              >
                {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </Box>

            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
              <LocalText>Showing</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>
                {totalCount === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)}
              </Box>{' '}
              <LocalText>of</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>{totalCount}</Box>{' '}
              <LocalText>deposits</LocalText>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.75}>
            <Box component="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: page === 0 ? '#cbd5e1' : '#64748b', cursor: page === 0 ? 'not-allowed' : 'pointer', '&:hover:not(:disabled)': { bgcolor: '#f8fafc' } }}>
              <LocalText>Previous</LocalText>
            </Box>

            {Array.from({ length: totalPages }, (_, i) => (
              <Box key={i} component="button" onClick={() => setPage(i)}
                sx={{ minWidth: 32, py: 0.75, border: '1px solid', borderRadius: '8px', fontSize: 12, fontWeight: 700, cursor: 'pointer', ...(i === page ? { bgcolor: '#0a326d', color: 'white', borderColor: '#0a326d' } : { bgcolor: 'white', color: '#64748b', borderColor: '#dde6f0', '&:hover': { bgcolor: '#f8fafc' } }) }}>
                {i + 1}
              </Box>
            ))}

            <Box component="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: page >= totalPages - 1 ? '#cbd5e1' : '#64748b', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', '&:hover:not(:disabled)': { bgcolor: '#f8fafc' } }}>
              <LocalText>Next</LocalText>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DepositsTable;
